import { logger } from "@octopusdeploy/logging";
import type { AccountResource, ActionTemplateSearchResource, ProcessTemplateResource, FeedResource, GitCredentialResource, ModifyProcessCommand, PersistenceSettings, ProcessResource, GitHubAppConnectionSummary, GetProcessTemplateByVersionMaskResponse, } from "@octopusdeploy/octopus-server-client";
import { HasRunbooksInGit, HasGitPersistenceSettings, isModifyDeploymentProcessCommand, isModifyRunbookProcessCommand, OctopusError, Permission, Repository } from "@octopusdeploy/octopus-server-client";
import flatMap from "lodash/flatMap";
import uniq from "lodash/uniq";
import React, { useCallback, useEffect, useMemo } from "react";
import { ProcessAccountsContextProvider } from "~/areas/projects/components/Process/Contexts/ProcessAccountsContextProvider";
import { ProcessGitCredentialsContextProvider } from "~/areas/projects/components/Process/Contexts/ProcessGitCredentialsContextProvider";
import { ProcessTemplatesContextProvider } from "~/areas/projects/components/Process/Contexts/ProcessTemplatesContextProvider";
import { getProcessTemplateSlugFromAction, getProcessTemplateVersionMaskFromAction } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import { repository } from "~/clientInstance";
import type { DoBusyTask, Errors } from "~/components/DataBaseComponent";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent";
import { createErrorsFromOctopusError } from "~/components/DataBaseComponent/Errors";
import { DevToolsTab } from "~/components/DevTools/DevToolsContext";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import pluginRegistry from "../../../../../components/Actions/pluginRegistry";
import type { ProcessContextModelState, ProcessIdentifier, ProcessPageSupportedActions } from "../types";
import { isProjectTemplateProcessIdentifier, isProcessOrProjectTemplateProcessIdentifier, isDeploymentOrRunbookProcessIdentifier } from "../types";
import { useGitHubConnectionRepositorySourcingEnabled } from "../useGitHubConnectionRepositorySourcingEnabled";
import { DevToolbarProcessUpload } from "./DeploymentProcessUpload";
import { ProcessActionTemplatesContextProvider } from "./ProcessActionTemplatesContextProvider";
import type { ProcessContextLookupState, ProcessContextProps, ProcessContextProviderSetupActions } from "./ProcessContext";
import { ProcessContext, useBoundProcessActions } from "./ProcessContext";
import type { ProcessStateSelectors } from "./ProcessContextState";
import { getProcessContextModelInitialState, getSelectors, processContextModelStateReducer } from "./ProcessContextState";
import { ProcessErrorsController } from "./ProcessErrors/ProcessErrorsContext";
import { ProcessFeedsContextProvider } from "./ProcessFeedsContextProvider";
import { ProcessGitHubConnectionsContextProvider } from "./ProcessGitHubConnectionsContextProvider";
import { ProcessSearchFilterController } from "./ProcessSearchFilter/ProcessSearchFilterContext";
import { ProcessWarningsController } from "./ProcessWarnings/ProcessWarningsContext";
interface ProcessControllerProps {
    doBusyTask: DoBusyTask;
    children: (renderProps: ProcessContextProps) => React.ReactNode;
    layoutActions: ProcessPageSupportedActions;
    errors?: Errors;
    processIdentifier: ProcessIdentifier;
    process: ProcessResource;
    reloadProcess: () => void;
    modifyProcess: (process: ModifyProcessCommand, gitRef: string | undefined) => Promise<void>;
}
export type FoundProcessTemplateDetails = {
    slug: string;
    versionMask: string;
    processTemplate: ProcessTemplateResource;
    processTemplateVersion: string;
};
export type NotFoundProcessTemplateDetails = {
    slug: string;
    versionMask: string;
    processTemplate: "NotFound";
    processTemplateVersion: undefined;
};
export type ProcessTemplateDetails = FoundProcessTemplateDetails | NotFoundProcessTemplateDetails;
const useProcessState = () => {
    return React.useState<ProcessContextLookupState>({
        actionTemplates: "NotLoaded",
        feeds: "NotLoaded",
        accounts: [],
        gitCredentials: [],
        gitHubConnections: [],
        processTemplates: "NotLoaded",
    });
};
const getStateUpdaters = (setState: React.Dispatch<React.SetStateAction<ProcessContextLookupState>>) => {
    return {
        onActionTemplatesUpdated: (actionTemplates: ActionTemplateSearchResource[]) => setState((current) => ({ ...current, actionTemplates })),
        onFeedsUpdated: (feeds: FeedResource[]) => setState((current) => ({ ...current, feeds })),
        onAccountsUpdated: (accounts: AccountResource[]) => setState((current) => ({ ...current, accounts })),
        onGitCredentialsUpdated: (gitCredentials: GitCredentialResource[]) => setState((current) => ({ ...current, gitCredentials })),
        onGitHubConnectionsUpdated: (gitHubConnections: GitHubAppConnectionSummary[]) => setState((current) => ({ ...current, gitHubConnections })),
        onProcessTemplatesUpdated: (processTemplates: ProcessTemplateDetails[]) => setState((current) => ({ ...current, processTemplates })),
    };
};
const useSelectors = (state: ProcessContextModelState): ProcessStateSelectors => {
    return React.useMemo(() => getSelectors(state), [state]);
};
export const ProcessController: React.FC<ProcessControllerProps> = ({ children, doBusyTask, layoutActions, processIdentifier, process, reloadProcess, modifyProcess }: ProcessControllerProps) => {
    const allowTemplatedProjectProcessEditorView = useOctopusFeatureToggle("allow-templated-project-process-editor-view", false);
    const [state, dispatch] = React.useReducer(processContextModelStateReducer, getProcessContextModelInitialState(processIdentifier, allowTemplatedProjectProcessEditorView));
    const selectors = useSelectors(state);
    const [lookupsState, setState] = useProcessState();
    const { setProcess: setProcessDispatchAction, conflictDetected: conflictDetectedDispatchAction, ...boundDispatchActions } = useBoundProcessActions(dispatch);
    const gitHubConnectionRepositorySourcingEnabled = useGitHubConnectionRepositorySourcingEnabled();
    const setProcess = useCallback(async (process: ProcessResource, updateCleanModel: boolean) => {
        const allPlugins = await Promise.all(uniq(flatMap(process.Steps, (step) => step.Actions).map((action) => pluginRegistry.getAction(action.ActionType, action.StepPackageVersion))));
        setProcessDispatchAction(process, updateCleanModel, allPlugins);
    }, [setProcessDispatchAction]);
    const boundActions = {
        ...boundDispatchActions,
        setProcess,
        conflictDetected: async (serverProcess: ProcessResource, stagedProcess: ProcessResource) => {
            const allPlugins = await Promise.all(uniq(flatMap([...serverProcess.Steps, ...stagedProcess.Steps], (step) => step.Actions).map((action) => pluginRegistry.getAction(action.ActionType, action.StepPackageVersion))));
            conflictDetectedDispatchAction(serverProcess, stagedProcess, allPlugins);
        },
    };
    useEffect(() => {
        setProcess(process, true);
    }, [setProcess, process]);
    const stateUpdaters = React.useMemo(() => getStateUpdaters(setState), [setState]);
    const refreshAccounts = useDoBusyTaskEffect(doBusyTask, async () => {
        const accounts = await repository.Accounts.all();
        stateUpdaters.onAccountsUpdated(accounts);
    }, []);
    const refreshActionTemplates = useDoBusyTaskEffect(doBusyTask, async () => {
        const templates = await repository.ActionTemplatesV2.search();
        stateUpdaters.onActionTemplatesUpdated(templates);
    }, []);
    const refreshFeeds = useDoBusyTaskEffect(doBusyTask, async () => {
        if (isProcessOrProjectTemplateProcessIdentifier(processIdentifier)) {
            stateUpdaters.onFeedsUpdated([]);
            return;
        }
        const feeds = isAllowed({ permission: Permission.FeedView, project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined, wildcard: true }) ? await repository.Feeds.all() : [];
        stateUpdaters.onFeedsUpdated(feeds);
    }, []);
    const refreshGitCredentials = useDoBusyTaskEffect(doBusyTask, async () => {
        if (isProcessOrProjectTemplateProcessIdentifier(processIdentifier)) {
            const platformHubGitCredentials = isAllowed({ permission: Permission.PlatformHubView, wildcard: true }) ? (await repository.PlatformHubGitCredentials.get({ Take: Repository.takeAll })).Items : [];
            // TODO: The editor should have first class support for GitCredentials
            // https://app.shortcut.com/octopusdeploy/story/116552/process-editor-first-class-support-for-ph-accounts-and-gitcredentials
            const asGitCredentials: GitCredentialResource[] = platformHubGitCredentials.map((c) => ({ ...c, SpaceId: "system", Links: { Self: "", Usage: "" } }));
            stateUpdaters.onGitCredentialsUpdated(asGitCredentials);
            return;
        }
        const gitCredentials = isAllowed({ permission: Permission.GitCredentialView, project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined, wildcard: true })
            ? (await repository.GitCredentials.list({ take: Repository.takeAll })).Items
            : [];
        stateUpdaters.onGitCredentialsUpdated(gitCredentials);
    }, []);
    const refreshGitHubConnections = useDoBusyTaskEffect(doBusyTask, async () => {
        if (!gitHubConnectionRepositorySourcingEnabled) {
            return;
        }
        if (isProcessOrProjectTemplateProcessIdentifier(processIdentifier)) {
            // TODO: Platform Hub GitHub Connections. These are not currently supported in the Platform Hub (as of October 2025).
            return;
        }
        const gitHubConnections = isAllowed({ permission: Permission.GitCredentialView, project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined, wildcard: true })
            ? (await repository.GitHubApp.getConnections(0, Repository.takeAll)).Connections
            : [];
        stateUpdaters.onGitHubConnectionsUpdated(gitHubConnections);
    }, []);
    const processTemplateDetails = useMemo(() => {
        if (state.model.process?.Id) {
            return new Set(Object.values(state.model.actions.byId)
                .flatMap((a) => {
                const slug = getProcessTemplateSlugFromAction(a);
                const versionMaskFromAction = getProcessTemplateVersionMaskFromAction(a);
                const versionMask = versionMaskFromAction === "" ? "X" : versionMaskFromAction;
                return { slug, versionMask };
            })
                .filter((details) => !!details.slug)
                .sort());
        }
        return "WaitingForProcess";
    }, [state.model.actions.byId, state.model.process?.Id]);
    const processTemplateDetailsString = processTemplateDetails === "WaitingForProcess" ? "WaitingForProcess" : JSON.stringify([...processTemplateDetails.values()]);
    const refreshProcessTemplatesForProcess = useDoBusyTaskEffect(doBusyTask, async () => {
        if (processTemplateDetails !== "WaitingForProcess") {
            const processTemplates: ProcessTemplateDetails[] = [];
            for (const processTemplateDetail of processTemplateDetails) {
                try {
                    const response: GetProcessTemplateByVersionMaskResponse = isProjectTemplateProcessIdentifier(processIdentifier)
                        ? await repository.ProcessTemplates.getByVersionMaskFromSystem(processTemplateDetail.slug, processTemplateDetail.versionMask)
                        : await repository.ProcessTemplates.getByVersionMask(processTemplateDetail.slug, processTemplateDetail.versionMask);
                    processTemplates.push({
                        slug: processTemplateDetail.slug,
                        versionMask: processTemplateDetail.versionMask,
                        processTemplate: response.ProcessTemplate,
                        processTemplateVersion: response.ProcessTemplateVersion,
                    });
                }
                catch (e) {
                    logger.warn("Couldn't load process template {processTemplateId}", { processTemplateId: processTemplateDetail });
                    processTemplates.push({
                        slug: processTemplateDetail.slug,
                        versionMask: processTemplateDetail.versionMask,
                        processTemplate: "NotFound",
                        processTemplateVersion: undefined,
                    });
                }
            }
            stateUpdaters.onProcessTemplatesUpdated(processTemplates);
        }
    }, [processTemplateDetailsString]);
    const saveOnServer = async (process: ModifyProcessCommand, onError: (errors: Errors) => void, onSuccess: () => void, gitRef?: string): Promise<boolean> => {
        try {
            await modifyProcess(process, gitRef);
            onSuccess();
            return true;
        }
        catch (e) {
            if (e instanceof OctopusError) {
                const errors = createErrorsFromOctopusError(e);
                onError(errors);
            }
            throw e;
        }
    };
    const cloneToDifferentProcess = async (process: ModifyProcessCommand, targetProjectPersistenceSettings: PersistenceSettings, onError: (errors: Errors) => void, onSuccess: () => void): Promise<void> => {
        await doBusyTask(async () => {
            if (isModifyRunbookProcessCommand(process)) {
                if (HasRunbooksInGit(targetProjectPersistenceSettings)) {
                    throw new Error("Cannot clone steps to a Git runbook.");
                }
                await repository.Runbooks.modifyRunbookProcess(process);
            }
            else if (isModifyDeploymentProcessCommand(process)) {
                if (HasGitPersistenceSettings(targetProjectPersistenceSettings)) {
                    throw new Error("Cannot clone steps to a Git deployment process.");
                }
                await repository.DeploymentProcesses.modify(process, process.ProjectId);
            }
            else {
                throw new Error("Only deployment and runbook processes can be cloned to.");
            }
        }, {
            onError,
            onSuccess,
        });
    };
    const actions: ProcessContextProviderSetupActions = {
        ...boundActions,
        saveOnServer,
        cloneToDifferentProcess,
        refreshFromServer: reloadProcess,
        ...layoutActions,
    };
    const contextValue: ProcessContextProps = {
        state,
        actions: actions,
        selectors,
    };
    return (<ProcessFeedsContextProvider feeds={lookupsState.feeds} refreshFeeds={refreshFeeds}>
            <ProcessActionTemplatesContextProvider templates={lookupsState.actionTemplates} refreshActionTemplates={refreshActionTemplates}>
                <ProcessAccountsContextProvider accounts={lookupsState.accounts} refreshAccounts={refreshAccounts}>
                    <ProcessGitCredentialsContextProvider gitCredentials={lookupsState.gitCredentials} refreshGitCredentials={refreshGitCredentials}>
                        <ProcessGitHubConnectionsContextProvider gitHubConnections={lookupsState.gitHubConnections} refreshGitHubConnections={async () => {
            refreshGitHubConnections();
        }}>
                            <ProcessTemplatesContextProvider processTemplates={lookupsState.processTemplates} refreshProcessTemplates={refreshProcessTemplatesForProcess}>
                                <ProcessContext.Provider value={contextValue}>
                                    <DevToolsTab name={`Upload ${contextValue.state.processIdentifier.type} Process`}>
                                        <DevToolbarProcessUpload processContext={contextValue}/>
                                    </DevToolsTab>
                                    <ProcessSearchFilterController processType={contextValue.state.processIdentifier.type} selectors={contextValue.selectors}>
                                        {() => (<ProcessErrorsController>
                                                <ProcessWarningsController>{children(contextValue)}</ProcessWarningsController>
                                            </ProcessErrorsController>)}
                                    </ProcessSearchFilterController>
                                </ProcessContext.Provider>
                            </ProcessTemplatesContextProvider>
                        </ProcessGitHubConnectionsContextProvider>
                    </ProcessGitCredentialsContextProvider>
                </ProcessAccountsContextProvider>
            </ProcessActionTemplatesContextProvider>
        </ProcessFeedsContextProvider>);
};
ProcessController.displayName = "ProcessController"
